import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import AddIcons from '@/icons/AddIcons'
import DeleteOutlineIcons from '@/icons/DeleteOutlineIcons'
import {
    getCurrentEmployee,
    updateCurrentEmployee,
} from '@/modules/Account/components/services'
import {
    getEmployeeInfo,
    updateEmployeeAttendances,
    updateEmployeeExperience,
} from '@/modules/Employee/services'
import { EmployeeConfimationValues } from '@/modules/Employee/types'
import { yupResolver } from '@hookform/resolvers/yup'
import React, { useEffect } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import {
    Button,
    GridHookForm,
    IconButton,
    Loading,
    Typogaphy,
} from 'vnetwork-ui'
import { FormValues } from '../../../Confirmation'
import { validateStep } from '../../../Profile/schema'

interface ExperienceProps {
    employeeId?: number
    isSelf?: boolean
}

const Experience: React.FC<ExperienceProps> = ({
    employeeId,
    isSelf = false,
}) => {
    const params = useParams()
    const id = employeeId || Number(params.id)
    const { t } = useTranslation()

    const { data, renderState } = useQueryWithHandler({
        queryKey: isSelf
            ? [QUERY_KEYS.CURRENT_USER_PROFILE]
            : [QUERY_KEYS.EMPLOYEE, id],
        queryFn: () => (isSelf ? getCurrentEmployee() : getEmployeeInfo(id)),
        enabled: isSelf || !!id,
        loadingComponent: (
            <div className="flex justify-center items-center min-h-[200px]">
                <Loading.CircularProgress width={40} height={40} />
            </div>
        ),
    })

    const {
        mutateAsync: updateExperienceMutation,
        isPending: isUpdatePending,
    } = useMutationWithHandler(
        {
            mutationFn: async (values: Partial<EmployeeConfimationValues>) => {
                const promises = []

                // Update Experience
                if (isSelf) {
                    promises.push(updateCurrentEmployee(values as any))
                } else {
                    promises.push(
                        updateEmployeeExperience({
                            id: Number(id),
                            employee: {
                                employee_university: values.employee_university,
                                employee_major: values.employee_major,
                                employee_experience: values.employee_experience,
                            } as Partial<FormValues>,
                        })
                    )
                }

                // Update attendances
                promises.push(
                    updateEmployeeAttendances({
                        id: Number(id),
                        employee: {
                            attendances: values.attendances,
                        } as Partial<FormValues>,
                    })
                )

                return Promise.all(promises)
            },
        },
        {
            reactQueryKeys: isSelf
                ? QUERY_KEYS.CURRENT_USER_PROFILE
                : QUERY_KEYS.EMPLOYEES,
            successMessage: 'employee-update-success',
            errorMessage: 'error-occurred',
        }
    )

    const formControl = useForm<any>({
        defaultValues: {
            employee_university: data?.result?.employee_university || '',
            employee_major: data?.result?.employee_major || '',
            employee_experience: data?.result?.employee_experience || '',
            attendances: data?.result?.attendances || [],
        },
        resolver: yupResolver(validateStep({ activeTab: 'Experience' }) as any),
    })

    useEffect(() => {
        formControl.reset({
            employee_university: data?.result?.employee_university || '',
            employee_major: data?.result?.employee_major || '',
            employee_experience: data?.result?.employee_experience || '',
            attendances: data?.result?.attendances || [],
        })
    }, [data])

    const { fields, append, remove } = useFieldArray({
        control: formControl.control,
        name: 'attendances',
    })

    const onSubmit = async (values: Partial<EmployeeConfimationValues>) => {
        await updateExperienceMutation(values)
    }

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <div className="">
            <GridHookForm
                actions={[]}
                subForm={true}
                formControl={formControl}
                className="grid grid-cols-1 md:grid-cols-2 gap-4"
                data={[
                    {
                        formItemProps: {
                            type: 'textfield',
                            name: 'employee_university',
                            placeholder: t('university'),
                            label: t('university'),
                        },
                        className: 'md:col-span-2',
                    },
                    {
                        formItemProps: {
                            type: 'textfield',
                            name: 'employee_major',
                            placeholder: t('major'),
                            label: t('major'),
                        },
                        className: 'md:col-span-2',
                    },
                    {
                        formItemProps: {
                            type: 'textarea',
                            name: 'employee_experience',
                            placeholder: t('experience'),
                            label: t('experience'),
                        },
                        className: 'md:col-span-2',
                    },
                ]}
            />
            <div className="bg-blue-50 rounded-md p-4">
                <div className="flex justify-between items-center mb-2">
                    <Typogaphy
                        size="medium"
                        className="font-semibold text-gray-900"
                    >
                        {t('attendances')} ({fields.length})
                    </Typogaphy>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcons />}
                        onClick={() =>
                            append({
                                name: '',
                                description: '',
                            })
                        }
                        className="bg-primary hover:bg-primary/80"
                    >
                        {t('attendances.add')}
                    </Button>
                </div>

                {fields.length > 0 &&
                    fields.map((field, index) => (
                        <GridHookForm
                            key={field.id}
                            actions={[]}
                            className="grid grid-cols-12 gap-4"
                            data={[
                                {
                                    formItemProps: {
                                        type: 'textfield',
                                        name: `attendances.${index}.name`,
                                        label: t('attendances.name'),
                                        placeholder: t('attendances.name'),
                                    },
                                    className: 'col-span-3',
                                },
                                {
                                    formItemProps: {
                                        type: 'textfield',
                                        name: `attendances.${index}.description`,
                                        label: t('attendances.description'),
                                        placeholder: t(
                                            'attendances.description'
                                        ),
                                    },
                                    className: 'col-span-8',
                                },
                                {
                                    renderEditComponent: () => (
                                        <div className="flex justify-end">
                                            <IconButton
                                                size="small"
                                                onClick={() => remove(index)}
                                            >
                                                <DeleteOutlineIcons className="text-red-500" />
                                            </IconButton>
                                        </div>
                                    ),
                                },
                            ]}
                            subForm
                            formControl={formControl}
                        />
                    ))}
            </div>
            <GridHookForm
                onSubmit={onSubmit}
                actions={[
                    {
                        title: t('save-changes'),
                        type: 'submit',
                        variant: 'contained',
                        loading: isUpdatePending,
                        disabled: !formControl.formState.isDirty,
                    },
                ]}
                data={[]}
                formControl={formControl}
                className="grid grid-cols-2 gap-4"
            />
        </div>
    )
}

export default Experience
